<template>
    <div class="claim-tree relative text-sm min-w-fit w-full">
        <template v-for="(item, index) in claimTree" :key="item.id">
            <div class="claim-tree__claim-item relative min-w-[120px] h-8" :class="isRoot ? 'pl-[16px]' : 'pl-[30px]'">
                <div class="flex items-center w-fit pr-4 cursor-pointer hover:bg-slate-100 py-1 w-full min-w-fit">
                    <template v-if="!isRoot">
                        <!-- 上竖线 -->
                        <span class="claim-tree__claim-line left-[5px] -top-1 bottom-1/2" />
                        <!-- 下竖线，如果是最后一个，则不显示 -->
                        <span v-if="index !== claimTree.length - 1" class="claim-tree__claim-line left-[5px] top-1/2 bottom-0" />
                        <!-- 横线 -->
                        <span class="absolute left-[5px] top-1/2 translate-y-[-2px] h-[2px] w-10 bg-gray-40" />
                    </template>
                    <div
                        v-if="item.children?.length"
                        class="relative shrink-0 self-stretch flex items-center justify-center px-2 cursor-pointer"
                        @click="toggleFold(item)"
                    >
                        <!-- 小竖线（连接 feature 的上竖线）折叠时不显示 -->
                        <span v-if="!item.isFolded" class="claim-tree__claim-line left-[15px] top-1/2 -bottom-2" />
                        <div
                            class="rounded z-0"
                            :class="{
                                'bg-yellow-500': isRoot,
                                'bg-gray-450': !isRoot,
                            }"
                        >
                            <SvgIcon
                                v-if="item.isFolded"
                                icon="SolidAddMedium"
                                class="w-4 h-4 fill-white"
                            />
                            <SvgIcon
                                v-else
                                icon="SolidMinusMedium"
                                class="w-4 h-4 fill-white"
                            />
                        </div>
                    </div>
                    <!-- 圆点 -->
                    <div v-else class="relative shrink-0 self-stretch flex items-center justify-center w-6 px-2 left-[1px] top-[1px]">
                        <span class="h-[6px] w-[6px] rounded-full bg-gray-40" />
                    </div>
                    <span class="text-gray-90 flex-shrink-0"> {{ t('aiSpecification.claimNum', { num: item.num }) }} </span>
                    <span class="ml-2 flex-grow overflow-hidden line-clamp-1 text-gray-1100">{{ item.simpleClaim }}</span>
                    <ElCheckbox
                        class="shrink-0 !h-auto"
                        :modelValue="isItemSelected(item)"
                        @update:modelValue="(val) => handleCheckboxChange(item, Boolean(val))"
                    />
                </div>
            </div>
            <div
                v-show="!item.isFolded"
                class="relative claim-tree__claim-features"
            >
                <!-- 长竖线，如果是最后一个，则不显示，根节点不显示 -->
                <span v-if="index !== claimTree.length - 1 && !isRoot" class="claim-tree__claim-line left-[5px] top-0 bottom-0" />

                <ClaimTree
                    :class="isRoot ? 'pl-[26px]' : 'pl-10'"
                    :claimTree="item.children || []"
                    :parentClaim="item"
                    :depth="depth + 1"
                    :modelValue="modelValue"
                    @update:modelValue="(val) => handleModelValueUpdate(val)"
                />
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import SvgIcon from '@telecom/shared/components/SvgIcon.vue';
import '@patsnap-ui/icon/assets/solid/MinusMedium.svg';
import '@patsnap-ui/icon/assets/solid/AddMedium.svg';
import type {
    SpecificationConfirmClaimItem,
    SpecificationConfirmClaimTree,
} from '@/domains/specification/types/specification-confirm-claims';
import { ElCheckbox } from 'element-plus';

const props = withDefaults(defineProps<{
    claimTree: SpecificationConfirmClaimTree
    parentClaim?: SpecificationConfirmClaimItem;
    depth?: number;
}>(), {
    depth: 0,
    parentClaim: undefined,
});

const modelValue = defineModel<SpecificationConfirmClaimItem[]>('modelValue', {
    default: () => [],
    required: true,
});

const { t } = useI18n();

function toggleFold(item: SpecificationConfirmClaimItem) {
    item.isFolded = !item.isFolded;
}

const isRoot = computed(() => props.depth === 0);

// 检查项目是否被选中
function isItemSelected(item: SpecificationConfirmClaimItem): boolean {
    return modelValue.value.some(selectedItem => selectedItem.id === item.id);
}

// 处理 checkbox 变化
function handleCheckboxChange(item: SpecificationConfirmClaimItem, checked: boolean) {
    const newValue = [...modelValue.value];
    if (checked) {
        // 添加当前项
        if (!newValue.some(selectedItem => selectedItem.id === item.id)) {
            newValue.push(item);
        }
    } else {
        // 移除当前项
        const index = newValue.findIndex(selectedItem => selectedItem.id === item.id);
        if (index !== -1) {
            newValue.splice(index, 1);
        }
    }
    modelValue.value = newValue;
}

// 处理子组件的 modelValue 更新
function handleModelValueUpdate(newValue: SpecificationConfirmClaimItem[]) {
    modelValue.value = newValue;
}
</script>

<style lang="scss">
.claim-tree {
    &__claim-line {
        @apply absolute w-[2px] bg-gray-40;
    }
    .el-checkbox__inner {
        @apply rounded;
    }
}
</style>
